import type { SortDescriptor } from "react-stately";

import { UserRoleEnum } from "@nlc/shared-types";
import { memo, useState } from "react";

import { useUser } from "@/hooks/useUser";

import type { RequestFilterType } from "../../../types/filters";
import type { DragDropHandlers, QueuedRequest } from "../../../types/tables";

import {
  queuedDetailsNeededTableConfig,
  queuedInQueueTableConfig,
  queuedPausedTableConfig,
} from "../../../config/tables/customer/queuedTableConfigs";
import { useQueuedRequests, useReorderQueueRequests } from "../../../hooks";
import { useResetFilterOnEmptySearch } from "../../../hooks/shared";
import { ConfigurableTable } from "../../tables";

// Drag and drop handlers for queue reordering
const createDragDropHandlers = (
  _tableId: string,
  reorderMutation: ReturnType<typeof useReorderQueueRequests>,
): DragDropHandlers<QueuedRequest> => ({
  onReorder: (
    _oldIndex: number,
    _newIndex: number,
    _reorderedData: QueuedRequest[],
  ) => {
    // For immediate UI feedback, we don't need to do anything here
    // The table component handles the visual reordering
  },
  onSave: async (reorderedData: QueuedRequest[]) => {
    // Extract the IDs in their new order
    const orderedRequestIds = reorderedData.map((request) => request.id);

    await reorderMutation.mutateAsync(orderedRequestIds);
  },
});

interface QueuedTabProps {
  onFilterChange?: (filter: RequestFilterType) => void;
  searchValue?: string;
  selectedFilter?: RequestFilterType;
}

function QueuedTabComponent({
  onFilterChange,
  searchValue,
  selectedFilter,
}: QueuedTabProps) {
  const { data: userData } = useUser();

  // Check if user can reorder queue (Owner or Administrator only)
  const canReorderQueue =
    userData?.user?.roleId === UserRoleEnum.Owner ||
    userData?.user?.roleId === UserRoleEnum.Administrator;

  // Reorder mutation for queue management
  const reorderMutation = useReorderQueueRequests({
    onError: (_error) => {
      // Error handled by toast in the hook
    },
    onSuccess: () => {
      // Success feedback could be added here if needed
    },
  });

  // Separate sort descriptors for each table
  const [inQueueSortDescriptor, setInQueueSortDescriptor] =
    useState<SortDescriptor>({
      column: queuedInQueueTableConfig.defaultSort?.column || "name",
      direction:
        queuedInQueueTableConfig.defaultSort?.direction === "desc"
          ? "descending"
          : "ascending",
    });

  const [detailsNeededSortDescriptor, setDetailsNeededSortDescriptor] =
    useState<SortDescriptor>({
      column: queuedDetailsNeededTableConfig.defaultSort?.column || "createdAt",
      direction:
        queuedDetailsNeededTableConfig.defaultSort?.direction === "desc"
          ? "descending"
          : "ascending",
    });

  const [pausedSortDescriptor, setPausedSortDescriptor] =
    useState<SortDescriptor>({
      column: queuedPausedTableConfig.defaultSort?.column || "createdAt",
      direction:
        queuedPausedTableConfig.defaultSort?.direction === "desc"
          ? "descending"
          : "ascending",
    });

  // Pagination state for each queue type
  const [inQueuePage, setInQueuePage] = useState(1);
  const [inQueueItemsPerPage, setInQueueItemsPerPage] = useState(25);
  const [detailsNeededPage, setDetailsNeededPage] = useState(1);
  const [detailsNeededItemsPerPage, setDetailsNeededItemsPerPage] =
    useState(25);
  const [pausedPage, setPausedPage] = useState(1);
  const [pausedItemsPerPage, setPausedItemsPerPage] = useState(25);

  // Get data for each queue section separately
  // Note: Don't pass sortDescriptor, limit, or page for inQueue - let backend return all results for drag-and-drop
  const {
    data: inQueueData,
    isFetching: isInQueueFetching,
    isLoading: isInQueueLoading,
    isSuccess: isInQueueSuccess,
  } = useQueuedRequests({
    filter: selectedFilter,
    queueSection: "inQueue",
    search: searchValue,
    // No limit/page - returns all queued requests for drag-and-drop reordering
  });

  const {
    data: detailsNeededData,
    isFetching: isDetailsNeededFetching,
    isLoading: isDetailsNeededLoading,
    isSuccess: isDetailsNeededSuccess,
  } = useQueuedRequests({
    filter: selectedFilter,
    limit: detailsNeededItemsPerPage,
    page: detailsNeededPage,
    queueSection: "detailsNeeded",
    search: searchValue,
    sortDescriptor: detailsNeededSortDescriptor,
  });

  const {
    data: pausedData,
    isFetching: isPausedFetching,
    isLoading: isPausedLoading,
    isSuccess: isPausedSuccess,
  } = useQueuedRequests({
    filter: selectedFilter,
    limit: pausedItemsPerPage,
    page: pausedPage,
    queueSection: "paused",
    search: searchValue,
    sortDescriptor: pausedSortDescriptor,
  });

  useResetFilterOnEmptySearch({
    // Never the broadest scope ("All" tab is), so always escalate.
    isAlreadyBroadest: false,
    isReady:
      isInQueueSuccess &&
      !isInQueueFetching &&
      isDetailsNeededSuccess &&
      !isDetailsNeededFetching &&
      isPausedSuccess &&
      !isPausedFetching,
    onSwitchToAll: () => onFilterChange?.("all"),
    searchTerm: searchValue,
    totalMatches:
      (inQueueData?.total ?? 0) +
      (detailsNeededData?.total ?? 0) +
      (pausedData?.total ?? 0),
  });

  // In Queue pagination handlers
  const handleInQueuePageChange = (page: number) => setInQueuePage(page);
  const handleInQueueItemsPerPageChange = (itemsPerPage: number) => {
    setInQueueItemsPerPage(itemsPerPage);
    setInQueuePage(1);
  };

  // Details Needed pagination handlers
  const handleDetailsNeededPageChange = (page: number) =>
    setDetailsNeededPage(page);
  const handleDetailsNeededItemsPerPageChange = (itemsPerPage: number) => {
    setDetailsNeededItemsPerPage(itemsPerPage);
    setDetailsNeededPage(1);
  };

  // Paused pagination handlers
  const handlePausedPageChange = (page: number) => setPausedPage(page);
  const handlePausedItemsPerPageChange = (itemsPerPage: number) => {
    setPausedItemsPerPage(itemsPerPage);
    setPausedPage(1);
  };

  return (
    <div className="space-y-6">
      {/* In Queue Table */}
      <ConfigurableTable
        config={queuedInQueueTableConfig}
        data={inQueueData?.requests || []}
        dataSection="in-queue"
        dragDropHandlers={
          canReorderQueue
            ? createDragDropHandlers("in-queue", reorderMutation)
            : undefined
        }
        isLoading={isInQueueLoading}
        paginationProps={{
          metadata: {
            currentPage: inQueuePage,
            itemsPerPage: inQueueItemsPerPage,
            totalItems: inQueueData?.total ?? null,
            totalPages: inQueueData?.pagination?.totalPages ?? null,
          },
          onItemsPerPageChange: handleInQueueItemsPerPageChange,
          onPageChange: handleInQueuePageChange,
          pageSizeOptions: [10, 25, 50, 100],
          showPageSizeSelector: true,
        }}
        setSortDescriptor={setInQueueSortDescriptor}
        sortDescriptor={inQueueSortDescriptor}
        useAdvancedSorting={false}
      />

      {/* Details Needed Table */}
      <ConfigurableTable
        config={queuedDetailsNeededTableConfig}
        data={detailsNeededData?.requests || []}
        isLoading={isDetailsNeededLoading}
        paginationProps={{
          metadata: {
            currentPage: detailsNeededPage,
            itemsPerPage: detailsNeededItemsPerPage,
            totalItems: detailsNeededData?.total ?? null,
            totalPages: detailsNeededData?.pagination?.totalPages ?? null,
          },
          onItemsPerPageChange: handleDetailsNeededItemsPerPageChange,
          onPageChange: handleDetailsNeededPageChange,
          pageSizeOptions: [10, 25, 50, 100],
          showPageSizeSelector: true,
        }}
        setSortDescriptor={setDetailsNeededSortDescriptor}
        sortDescriptor={detailsNeededSortDescriptor}
        useAdvancedSorting={true}
      />

      {/* Paused Table */}
      <ConfigurableTable
        config={queuedPausedTableConfig}
        data={pausedData?.requests || []}
        isLoading={isPausedLoading}
        paginationProps={{
          metadata: {
            currentPage: pausedPage,
            itemsPerPage: pausedItemsPerPage,
            totalItems: pausedData?.total ?? null,
            totalPages: pausedData?.pagination?.totalPages ?? null,
          },
          onItemsPerPageChange: handlePausedItemsPerPageChange,
          onPageChange: handlePausedPageChange,
          pageSizeOptions: [10, 25, 50, 100],
          showPageSizeSelector: true,
        }}
        setSortDescriptor={setPausedSortDescriptor}
        sortDescriptor={pausedSortDescriptor}
        useAdvancedSorting={true}
      />
    </div>
  );
}

export const QueuedTab = memo(QueuedTabComponent);
